import {
  PRIVACY_RIGHTS,
  REQUEST_CHANNELS,
  REQUEST_OUTCOMES,
  REQUEST_RESPONSE_DAYS,
  REQUEST_STATUSES,
  formatDate,
  type PrivacyRight,
  type RequestChannel,
  type RequestStatus,
} from '@erp/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { api, ApiError, errorMessage } from '../../lib/api.ts';
import { formatDueOn, refreshRequests, type RequestBrief } from '../../lib/privacy-requests-api.ts';
import { useSession } from '../../lib/session.ts';
import { useToast } from '../../lib/toast.tsx';
import { Alert } from '../ui/alert.tsx';
import { Badge } from '../ui/badge.tsx';
import { Button } from '../ui/button.tsx';
import { Dialog } from '../ui/dialog.tsx';
import { Field, Input, Select } from '../ui/field.tsx';

const textarea = 'w-full rounded-lg border border-border bg-surface p-3 text-base sm:text-sm';

export const STATUS_TONES: Record<RequestStatus, 'neutral' | 'info' | 'success' | 'warning'> = {
  received: 'info',
  in_progress: 'info',
  in_prevention: 'warning',
  resolved: 'success',
  withdrawn: 'neutral',
  incomplete: 'neutral',
};

export function RequestStatusBadge({ status }: { status: RequestStatus }) {
  return <Badge tone={STATUS_TONES[status]}>{REQUEST_STATUSES[status]}</Badge>;
}

/** Folio, right and status (what anyone who sees it may know). */
export function RequestBriefList({ items, empty }: { items: RequestBrief[]; empty: string }) {
  if (items.length === 0) return <p className="text-sm text-muted-foreground">{empty}</p>;
  return (
    <ul className="divide-y divide-border text-sm">
      {items.map((r) => (
        <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
          <span>
            <span className="font-medium">
              N.º {r.folio} · {PRIVACY_RIGHTS[r.right].label}
            </span>
            <span className="block text-muted-foreground">
              {r.closedAt
                ? r.outcome
                  ? REQUEST_OUTCOMES[r.outcome]
                  : REQUEST_STATUSES[r.status]
                : `Responder antes del ${formatDueOn(r.dueOn)}`}
            </span>
          </span>
          <RequestStatusBadge status={r.status} />
        </li>
      ))}
    </ul>
  );
}

/**
 * Anyone registers the reception of a request, as soon as it arrives: the 20 business
 * days to answer start then. Whoever handles privacy takes it from there.
 */
export function RegisterRequestDialog({
  contact,
  onClose,
}: {
  contact?: { id: string; name: string };
  onClose: () => void;
}) {
  const { data: session } = useSession();
  const client = useQueryClient();
  const notify = useToast();
  const [right, setRight] = useState<PrivacyRight | ''>('');
  const [channel, setChannel] = useState<RequestChannel | ''>('');
  const [requesterName, setRequesterName] = useState(contact?.name ?? '');
  const [replyTo, setReplyTo] = useState('');
  const [description, setDescription] = useState('');
  const [receivedOn, setReceivedOn] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const tz = session?.tenant.timezone;

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const created = await api<RequestBrief>('/privacy/requests', {
        method: 'POST',
        body: {
          right: right || undefined,
          channel: channel || undefined,
          requesterName,
          replyTo: replyTo.trim() || undefined,
          description,
          // A past day: noon of that day in El Salvador (the term counts by days).
          ...(receivedOn ? { receivedAt: `${receivedOn}T12:00:00-06:00` } : {}),
          contactId: contact?.id ?? null,
        },
      });
      await refreshRequests(client);
      notify(
        `Solicitud n.º ${String(created.folio)} registrada: se responde antes del ${formatDueOn(created.dueOn)}.`,
        'success',
      );
      onClose();
    } catch (e) {
      setError(
        e instanceof ApiError && e.issues.length > 0
          ? (e.issues[0]?.message ?? errorMessage(e))
          : errorMessage(e),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title="Registrar una solicitud sobre datos personales"
      description={`Regístrala el mismo día que llega: la ley da ${String(REQUEST_RESPONSE_DAYS)} días hábiles para responder. Quien gestiona la privacidad la atiende.`}
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <fieldset className="space-y-1">
          <legend className="mb-1 text-sm font-medium">¿Qué pide?</legend>
          {(Object.keys(PRIVACY_RIGHTS) as PrivacyRight[]).map((r) => (
            <label key={r} className="flex min-h-11 items-start gap-3 text-sm">
              <input
                type="radio"
                name="right"
                className="mt-0.5 size-5"
                checked={right === r}
                onChange={() => {
                  setRight(r);
                }}
              />
              <span>
                <span className="font-medium">{PRIVACY_RIGHTS[r].label}</span>
                <span className="block text-muted-foreground">{PRIVACY_RIGHTS[r].help}</span>
              </span>
            </label>
          ))}
        </fieldset>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Quién lo pide">
            {(a11y) => (
              <Input
                {...a11y}
                maxLength={200}
                value={requesterName}
                onChange={(e) => {
                  setRequesterName(e.target.value);
                }}
              />
            )}
          </Field>
          <Field label="Por dónde llegó">
            {(a11y) => (
              <Select
                {...a11y}
                value={channel}
                onChange={(e) => {
                  setChannel(e.target.value as RequestChannel);
                }}
              >
                <option value="">Elige…</option>
                {Object.entries(REQUEST_CHANNELS).map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Cómo responderle" hint="Correo, teléfono o dirección (opcional).">
            {(a11y) => (
              <Input
                {...a11y}
                maxLength={300}
                value={replyTo}
                onChange={(e) => {
                  setReplyTo(e.target.value);
                }}
              />
            )}
          </Field>
          <Field
            label="Fecha en que llegó"
            hint={`Si está vacía: hoy, ${formatDate(new Date(), tz)}.`}
          >
            {(a11y) => (
              <Input
                {...a11y}
                type="date"
                value={receivedOn}
                onChange={(e) => {
                  setReceivedOn(e.target.value);
                }}
              />
            )}
          </Field>
        </div>
        <Field label="Qué pide exactamente">
          {(a11y) => (
            <textarea
              {...a11y}
              rows={4}
              maxLength={5000}
              className={textarea}
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
              }}
            />
          )}
        </Field>
        {contact && (
          <p className="text-sm text-muted-foreground">
            Se vincula con el contacto {contact.name}.
          </p>
        )}
        {error && <Alert tone="danger">{error}</Alert>}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" variant="brand" loading={saving}>
            Registrar
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
